import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Loader2, FileText, Lock, AlertTriangle, Receipt, Undo2, Landmark } from "lucide-react";
import { RemindersPanel } from "@/admin/RemindersPanel";
import { PaymentDialog } from "@/admin/PaymentDialog";
import { ChequeDialog } from "@/admin/ChequeDialog";

const STATUS_VARIANT = { paid: "default", partial: "secondary", pending: "outline" };
const todayStr = () => new Date().toISOString().slice(0, 10);

let lineSeq = 0;
const newLine = () => ({ product_id: "", qty: 1, discount_level: 0, above_list_price_per_bag: 0, _k: ++lineSeq });

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [payOrder, setPayOrder] = useState(null);
  const [chequeOrder, setChequeOrder] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    api.get("/admin/agents").then((r) => setAgents(r.data));
    api.get("/admin/customers").then((r) => setCustomers(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => p.active !== false)));
  }, []);

  const newOrder = () => {
    setForm({
      agent_id: "", customer_id: "", date: todayStr(), payment_status: "pending", paid_date: todayStr(), payment_mode: "bank_transfer", payment_reference: "",
      is_return: false, approved: true, credit_days: "", original_order_id: null, original_invoice_no: null,
      lines: [newLine()],
    });
    setOpen(true);
  };

  const newCreditNote = (o) => {
    setForm({
      agent_id: o.agent_id, customer_id: o.customer_id, date: todayStr(), payment_status: "pending", paid_date: todayStr(),
      is_return: true, approved: true, credit_days: "", original_order_id: o.id, original_invoice_no: o.invoice_no,
      lines: o.lines.map((l) => ({ ...newLine(), product_id: l.product_id, qty: l.qty, discount_level: l.discount_level || 0, above_list_price_per_bag: l.above_list_price_per_bag || 0 })),
    });
    setOpen(true);
  };

  const addLine = () =>
    setForm({ ...form, lines: [...form.lines, newLine()] });
  const setLine = (i, key, v) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [key]: v };
    setForm({ ...form, lines });
  };
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) });

  const selectedCustomer = customers.find((c) => c.id === form?.customer_id);
  const preview = useMemo(() => {
    if (!form) return { value: 0, comm: 0 };
    let value = 0, comm = 0;
    form.lines.forEach((l) => {
      const p = products.find((x) => x.id === l.product_id);
      if (!p) return;
      const lvl = Number(l.discount_level || 0), qty = Number(l.qty || 0), above = Number(l.above_list_price_per_bag || 0);
      value += qty * ((p.discount_prices?.[lvl] || 0) + above);
      comm += qty * (p.discount_commissions?.[lvl] || 0);
    });
    return { value, comm };
  }, [form, products]);

  const save = async () => {
    if (!form.agent_id || !form.customer_id) return toast.error("Select agent and customer");
    if (form.lines.some((l) => !l.product_id)) return toast.error("Choose a product on every line");
    setSaving(true);
    try {
      const { original_invoice_no, ...rest } = form;
      const payload = {
        ...rest,
        credit_days: form.credit_days === "" ? null : Number(form.credit_days),
        paid_date: form.payment_status === "paid" ? form.paid_date : null,
        lines: form.lines.map((l) => ({
          product_id: l.product_id, qty: Number(l.qty),
          discount_level: Number(l.discount_level),
          above_list_price_per_bag: Number(l.above_list_price_per_bag || 0),
        })),
      };
      const { data } = await api.post("/admin/orders", payload);
      toast.success(`${form.is_return ? "Credit note" : "Invoice"} ${data.invoice_no} created`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const patchStatus = async (oid, body) => {
    try {
      await api.patch(`/admin/orders/${oid}/status`, body);
      toast.success("Invoice updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const agentName = (id) => agents.find((a) => a.id === id)?.name || "—";
  const orderBags = (o) => o.lines.reduce((s, l) => s + l.qty, 0);
  const visible = (orders || []).filter((o) =>
    filter === "all" ? true : filter === "overdue" ? o.overdue : filter === "unpaid" ? (!o.is_return && o.payment_status !== "paid") : filter === "credit notes" ? o.is_return : (!o.is_return && o.payment_status === "paid"));

  return (
    <div>
      <PageHeader
        title="Invoices / Sales"
        subtitle="Every sale is an invoice. Prices and commission are frozen on the invoice; commission is earned only when the customer pays in full."
        actions={<Button onClick={newOrder} data-testid="add-order-button"><Plus className="w-4 h-4 mr-1" /> New invoice</Button>}
      />
      <RemindersPanel onSent={load} />
      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "unpaid", "overdue", "paid", "credit notes"].map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} className="capitalize" data-testid={`invoice-filter-${f.replace(" ", "-")}`}>{f}</Button>
        ))}
      </div>
      {!orders ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <Empty>No invoices in this view.</Empty>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-4 py-3">Invoice</th>
                  <th className="text-left font-medium px-4 py-3">Date</th>
                  <th className="text-left font-medium px-4 py-3">Due</th>
                  <th className="text-left font-medium px-4 py-3">Agent</th>
                  <th className="text-left font-medium px-4 py-3">Customer</th>
                  <th className="text-right font-medium px-4 py-3">Bags</th>
                  <th className="text-right font-medium px-4 py-3">Value</th>
                  <th className="text-right font-medium px-4 py-3">Balance</th>
                  <th className="text-right font-medium px-4 py-3">Comm.</th>
                  <th className="text-left font-medium px-4 py-3">Payment</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((o) => (
                  <tr key={o.id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link to={`/invoice/${o.id}`} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`invoice-link-${o.id}`}>
                        {o.is_return ? <Undo2 className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}{o.invoice_no || "—"}
                      </Link>
                      {o.credit_note_for && <div className="text-[10px] text-muted-foreground">against {o.credit_note_for}</div>}
                      {o.month_closed && <Lock className="w-3 h-3 inline ml-1 text-muted-foreground" title="Month closed" />}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{o.date}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {o.is_return ? "—" : o.due_date}
                      {o.overdue && <Badge variant="destructive" className="ml-1.5 gap-1"><AlertTriangle className="w-3 h-3" />overdue</Badge>}
                    </td>
                    <td className="px-4 py-3">{agentName(o.agent_id)}</td>
                    <td className="px-4 py-3">
                      {o.customer_name}
                      {o.is_return && <Badge variant="destructive" className="ml-2">Credit note</Badge>}
                      {o.commission_void && <Badge variant="outline" className="ml-2">Comm. void</Badge>}
                      {o.bounced_cheque && <Badge variant="destructive" className="ml-2">Cheque bounced</Badge>}
                      {o.approval_status === "pending" && <Link to="/dashboard/approvals"><Badge variant="secondary" className="ml-2">Awaiting approval · {o.max_discount_level}%</Badge></Link>}
                      {o.approval_status === "rejected" && <Badge variant="destructive" className="ml-2">Rejected</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">{orderBags(o)}</td>
                    <td className="px-4 py-3 text-right">{o.is_return ? `- ${money(o.sale_value)}` : money(o.sale_value)}</td>
                    <td className={`px-4 py-3 text-right ${o.balance_due > 0 ? "font-medium" : "text-muted-foreground"}`} data-testid={`order-balance-${o.id}`}>{o.is_return ? "—" : money(o.balance_due)}</td>
                    <td className="px-4 py-3 text-right text-primary">{o.is_return ? `- ${money(o.commission)}` : money(o.commission)}</td>
                    <td className="px-4 py-3">
                      {o.is_return ? <span className="text-xs text-muted-foreground">applied</span> : (
                        <button onClick={() => setPayOrder(o)} className="text-left" data-testid={`order-status-${o.id}`}>
                          <Badge variant={STATUS_VARIANT[o.payment_status] || "outline"} className="capitalize">{o.payment_status === "partial" ? "partially paid" : o.payment_status}</Badge>
                          {o.paid_date && <p className="text-[11px] text-muted-foreground mt-1">settled {o.paid_date}</p>}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {!o.is_return && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => setPayOrder(o)} data-testid={`record-payment-${o.id}`}>
                            <Receipt className="w-3.5 h-3.5 mr-1" />{o.payment_status === "paid" ? "Receipts" : "Record payment"}
                          </Button>
                          {o.payment_status !== "paid" && <Button variant="ghost" size="sm" onClick={() => setChequeOrder(o)} data-testid={`cheque-${o.id}`}>
                            <Landmark className="w-3.5 h-3.5 mr-1" />PDC
                          </Button>}
                          <Button variant="ghost" size="sm" disabled={o.month_closed || o.approval_status === "pending"} onClick={() => newCreditNote(o)} data-testid={`credit-note-${o.id}`}>
                            <Undo2 className="w-3.5 h-3.5 mr-1" />Credit note
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" disabled={o.month_closed} onClick={() => patchStatus(o.id, { commission_void: !o.commission_void })} data-testid={`void-toggle-${o.id}`}>
                        {o.commission_void ? "Restore comm." : "Void comm."}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <PaymentDialog order={payOrder} onClose={() => setPayOrder(null)} onChanged={load} />
      <ChequeDialog order={chequeOrder} onClose={() => setChequeOrder(null)} onChanged={load} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle data-testid="order-dialog-title">{form?.is_return ? `Credit note against ${form.original_invoice_no}` : "New invoice"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              {form.is_return && <p className="text-xs text-muted-foreground rounded-md bg-secondary/50 p-3" data-testid="credit-note-hint">Returned bags are credited at the prices frozen on the original invoice. The credit reduces the invoice balance and the agent's commission for those bags is reversed.</p>}
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Agent">
                  <Select value={form.agent_id} onValueChange={(v) => setForm({ ...form, agent_id: v })} disabled={form.is_return}>
                    <SelectTrigger data-testid="order-agent-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Fld>
                <Fld label="Customer">
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} disabled={form.is_return}>
                    <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Fld>
                <Fld label={form.is_return ? "Credit note date" : "Invoice date"}><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="order-date-input" /></Fld>
                {!form.is_return && (
                  <>
                    <Fld label={`Credit days (customer default ${selectedCustomer?.credit_days ?? "—"})`}>
                      <Input type="number" placeholder={String(selectedCustomer?.credit_days ?? 30)} value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: e.target.value })} data-testid="order-credit-days-input" />
                    </Fld>
                    <Fld label="Payment status">
                      <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                        <SelectTrigger data-testid="order-payment-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending - on credit</SelectItem>
                          <SelectItem value="paid">Paid in full now</SelectItem>
                        </SelectContent>
                      </Select>
                    </Fld>
                    {form.payment_status === "paid" && (
                      <>
                        <Fld label="Payment cleared on"><Input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} data-testid="order-paid-date-input" /></Fld>
                        <Fld label="Payment mode">
                          <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                            <SelectTrigger data-testid="order-payment-mode-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {["cash", "bank_transfer", "cheque", "card", "online"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace("_", " ")}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </Fld>
                        <Fld label="Reference"><Input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} data-testid="order-payment-reference-input" /></Fld>
                      </>
                    )}
                  </>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Line items</Label>
                  <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-button"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
                </div>
                <div className="space-y-2">
                  {form.lines.map((l, i) => (
                    <div key={l._k} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-5">
                        <Select value={l.product_id} onValueChange={(v) => setLine(i, "product_id", v)}>
                          <SelectTrigger className="h-9" data-testid={`line-product-${i}`}><SelectValue placeholder="Product" /></SelectTrigger>
                          <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.pack} · {p.list_price} AED</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Input className="col-span-2 h-9" type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`line-qty-${i}`} />
                      <div className="col-span-2">
                        <Select value={String(l.discount_level)} onValueChange={(v) => setLine(i, "discount_level", v)}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((d) => <SelectItem key={d} value={String(d)}>{d}%{d > 5 ? " · needs approval" : ""}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Input className="col-span-2 h-9" type="number" placeholder="+above/bag" value={l.above_list_price_per_bag} onChange={(e) => setLine(i, "above_list_price_per_bag", e.target.value)} />
                      <button className="col-span-1 text-muted-foreground hover:text-destructive" onClick={() => rmLine(i)}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                {form.lines.some((l) => Number(l.discount_level) > 5) && <p className="text-xs text-amber-700 dark:text-amber-400 mt-2" data-testid="approval-warning">A line is discounted above 5% - this invoice will wait for manager approval before it counts.</p>}
                <div className="flex justify-end gap-6 mt-3 text-sm" data-testid="invoice-preview-totals">
                  <span className="text-muted-foreground">Invoice value <b className="text-foreground">{money(preview.value)}</b></span>
                  <span className="text-muted-foreground">Agent commission <b className="text-primary">{money(preview.comm)}</b></span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="save-order-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : form?.is_return ? "Issue credit note" : "Create invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
